import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { GradientButton, IconButton } from '../../components/Buttons';
import BodyWrap from '../../components/BodyWrap';
import { IconUserCheck, IconUserOff, IconUserPlus } from '@tabler/icons-react';
import { Modal, Select, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
// import { format, parseISO } from 'date-fns';
import { clearLocalStorage } from '../../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types/User';
import { ErrorMessage } from '../../components/Messages';
import { UserStatus } from '../../components/Components';
import { ErrorResponseData } from '../../types/Common';

function Users() {
  useEffect(() => {
    document.title = 'Users | PromptSlate';
  }, []);
  const navigate = useNavigate();
  let [userListMessage, setUserListMessage] = useState<string>('');
  let [userInviteMessage, setUserInviteMessage] = useState<string>('');

  const [inviteUserModalOpened, setInviteUserModalOpened] = useState(false);
  let [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    error,
    data: users,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['users'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/user`, {
            headers: headers,
          });
          const users: User[] = response.data.users;
          return users;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setUserListMessage('Unable to fetch users');
            return [];
          } else {
            setUserListMessage('A network error occured. Please try again later.');
          }
        } else {
          setUserListMessage('An error occured. Please try again later.');
          return [];
        }
      }
    },
  });

  const handleInviteUserModalToggle = (opened: boolean) => {
    setInviteUserModalOpened(opened);
  };

  const inviteUserForm = useForm({
    initialValues: {
      name: '',
      email: '',
      accountType: '',
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return 'Enter name of the user';
        }
      },
      email: (value: string) => {
        if (!value) {
          return 'Enter email of the user to be invited';
        }
      },
      accountType: (value: string) => {
        if (!value) {
          return 'Choose a role for the user';
        }
        if (value !== 'ADMIN' && value !== 'USER') {
          return 'Choose a valid role for the user';
        }
      },
    },
  });

  const handleInviteUserSubmit = async (values: any) => {
    setIsLoading(true);
    setUserInviteMessage('');
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/user/invite`,
        {
          name: values.name,
          email: values.email,
          account_type: values.accountType,
        },
        {
          headers: headers,
        }
      );
      values.newEnvName = '';
      refetchUsers();
      setIsLoading(false);
      setInviteUserModalOpened(false);
    } catch (error) {
      setIsLoading(false);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const data = error.response?.data as ErrorResponseData;
        const message = data?.message ?? 'An unexpected error occurred.';

        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        } else {
          setUserInviteMessage(message);
        }
      } else {
        setUserInviteMessage('An error occured. Please try again later');
      }
    }
  };

  const handleUserStatusChange = async (email: string, currentStatus: string) => {
    setIsLoading(true);
    const newStatus = ['ACTIVE', 'INVITED'].includes(currentStatus) ? 'DISABLED' : 'ACTIVE';
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/user/status`,
        {
          email: email,
          status: newStatus,
        },
        {
          headers: headers,
        }
      );
      refetchUsers();
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          // setAPIKeyCreateMessage('Invalid request');
        } else {
          // setAPIKeyCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        // setAPIKeyCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  const validAccountTypes = [
    { value: 'ADMIN', label: 'Admin' },
    { value: 'USER', label: 'User' },
  ];

  return (
    <BodyWrap>
      <Modal
        opened={inviteUserModalOpened}
        onClose={() => handleInviteUserModalToggle(false)}
        title={<Text size={'xl'}>Invite a user</Text>}
        size={'lg'}
        radius="lg"
      >
        <form
          noValidate
          onSubmit={inviteUserForm.onSubmit((values) => handleInviteUserSubmit(values))}
          className="flex flex-col gap-5 pl-5 pr-5"
        >
          <TextInput
            className="mt-5"
            size="md"
            placeholder="Name of the user"
            {...inviteUserForm.getInputProps('name')}
          />
          <TextInput
            type="email"
            className="mt-2"
            size="md"
            placeholder="Email"
            {...inviteUserForm.getInputProps('email')}
          />
          <Select
            placeholder="Select a user role"
            data={validAccountTypes}
            value={inviteUserForm.values.accountType}
            onChange={(value) => {
              if (value) {
                inviteUserForm.setFieldValue('accountType', value);
              }
            }}
            allowDeselect={false}
            className="w-full mt-2 text-lg"
            styles={{
              input: {
                height: 45,
                paddingTop: 6,
                paddingBottom: 6,
              },
            }}
          />
          <ErrorMessage>{userInviteMessage}</ErrorMessage>
          <GradientButton className="mt-2 mb-5" type="submit">
            Invite user
          </GradientButton>
        </form>
      </Modal>
      <div className="text-md pb-5 ">Users</div>
      <IconButton title="Create a new API key" onClick={() => handleInviteUserModalToggle(true)}>
        <IconUserPlus size="1.5rem" className="mr-2" /> Invite user
      </IconButton>
      <div className="flex flex-col">
        {users ? (
          users.map((user) => {
            return (
              <div
                key={user.email}
                className="flex w-full mt-5 mr-5 p-5 bg-[#FFFFFF] border border rounded-md"
              >
                <div className="flex-auto w-2/12">{user.name}</div>
                <div className="flex-auto w-3/12">
                  <span className="">{user.email}</span>
                </div>
                <div className="flex-auto w-3/12">
                  <span className="">{user.account_type}</span>
                </div>
                <div className="flex-auto w-3/12">
                  <span className="">
                    <UserStatus status={user.status} />
                  </span>
                </div>
                {user.email === localStorage.getItem('email') ? (
                  <IconUserOff
                    size="1.5rem"
                    className="float-right cursor-pointer flex-auto w-1/12 text-slate-200"
                    title="You can't disable your own account"
                  />
                ) : user.status.toLowerCase() === 'disabled' ? (
                  <IconUserCheck
                    size="1.5rem"
                    className="text-green-500 w-1/12 hover:text-green-600 cursor-pointer inline-block"
                    title="Enable account"
                    onClick={() => handleUserStatusChange(user.email, user.status)}
                  />
                ) : (
                  <IconUserOff
                    size="1.5rem"
                    className="text-red-500 w-1/12 hover:text-red-600 cursor-pointer inline-block"
                    title="Disable account"
                    onClick={() => handleUserStatusChange(user.email, user.status)}
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className="w-full flex justify-center text-slate-600 ">Loading users</div>
        )}
      </div>
    </BodyWrap>
  );
}

export default Users;
